import {
  plansCreationStateSelector,
  setAllLimitByType,
  setLabel,
} from "@store/plan-creation/plan-creation-slice";
import { ProductType, BaseProduct } from "@utils/product/product-utils-types";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { TERA_BYTE_SIZE } from "@utils/limit-data-display";
import { AllAvailableTiers, TierType } from "@utils/limit-utils";
import { Dropdown } from "@flat-ui";

interface Props {
  /** The selected product in which a tier is going to be selected */
  selectedProduct: BaseProduct<ProductType> | null;
}

/** Tier selection as a plan limit */
export function PlanLimitTierSelection({
  selectedProduct,
}: Props): React.JSX.Element {
  const { allLimits } = useAppSelector(plansCreationStateSelector);
  const dispatch = useAppDispatch();

  // The tier is the one granting the storage of the plan. Selecting another product resets the
  // storage, which leaves no tier selected, while the storage of a copied plan selects its tier.
  const storageLimit = allLimits.find(({ type }) => type === "STORAGE")?.value;
  const selectedTier: TierType | "" =
    (Object.keys(AllAvailableTiers) as TierType[]).find(
      (tier) =>
        AllAvailableTiers[tier].storageLimit * TERA_BYTE_SIZE === storageLimit
    ) ?? "";

  function onTierChange(tier: TierType): void {
    dispatch(
      setAllLimitByType({
        type: "STORAGE",
        // Convert TB to GB
        value: AllAvailableTiers[tier].storageLimit * TERA_BYTE_SIZE,
        isEnabled: true,
      })
    );
    dispatch(
      setLabel(`${selectedProduct?.name} - ${AllAvailableTiers[tier].label}`)
    );
  }

  return (
    <Dropdown
      fullWidth
      label="Tier selection"
      value={selectedTier}
      onChange={(event) => onTierChange(event.target.value as TierType)}
      options={Object.entries(AllAvailableTiers).map(([tier]) => ({
        key: tier,
        value: tier,
        label: `${AllAvailableTiers[tier as TierType].label} - Storage limit: ${
          AllAvailableTiers[tier as TierType].storageLimit
        } TB`,
      }))}
    />
  );
}
